import { supabase } from './supabase';

/**
 * Can this record be removed, and if not, why not?
 *
 * Each guard returns `null` when deletion is safe, or a sentence explaining
 * what is in the way. One definition serves two callers:
 *
 *   - the confirmation dialog, which asks BEFORE showing a Delete button, so
 *     the user is never offered an action that will fail;
 *   - the delete mutation, which asks again at the moment of writing, because
 *     something may have been added in between.
 *
 * Keeping them in one place is what stops the dialog and the mutation
 * disagreeing — which is exactly how a dialog ends up telling somebody to
 * "mark it Completed" about a project that is already completed.
 */

export type Guard = () => Promise<string | null>;

/**
 * A head-only count on a table named at runtime.
 *
 * The table name is dynamic, so `supabase.from` has to be cast — and it is the
 * FUNCTION that needs casting, not its result: typing the result still leaves
 * the call itself rejecting a plain string. Nothing but the count travels
 * because of `head: true`.
 */
type CountQuery = {
  select: (c: string, o: { count: 'exact'; head: true }) => CountQuery;
  eq: (c: string, v: string) => CountQuery;
  is: (c: string, v: null) => PromiseLike<{ count: number | null; error: { message: string } | null }>;
};
const looseFrom = supabase.from.bind(supabase) as unknown as (table: string) => CountQuery;

async function count(table: string, column: string, id: string): Promise<number> {
  const { count: n, error } = await looseFrom(table)
    .select('id', { count: 'exact', head: true })
    .eq(column, id)
    .is('deleted_at', null);
  if (error) throw new Error(error.message);
  return n ?? 0;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

export const guardClient = (_id: string): Guard => async () => null;

export const guardProject = (_id: string): Guard => async () => null;

export const guardSupplier = (id: string): Guard => async () => {
  const purchases = await count('material_purchases', 'supplier_id', id);
  if (purchases === 0) return null;
  return `This supplier has ${plural(purchases, 'purchase')} recorded against them. ` +
    'Removing them would change your material and finance reports.';
};

export const guardSubcontractor = (id: string): Guard => async () => {
  const labour = await count('subcontractor_labour', 'subcontractor_id', id);
  if (labour === 0) return null;
  return `This sub-contractor has ${plural(labour, 'attendance record')}. ` +
    'Removing them would change your labour reports.';
};

export const guardMaterial = (id: string): Guard => async () => {
  const [purchases, usage] = await Promise.all([
    count('material_purchases', 'material_id', id),
    count('material_usage', 'material_id', id),
  ]);
  const total = purchases + usage;
  if (total === 0) return null;
  return `This material has ${plural(total, 'purchase or usage record')}. ` +
    'Removing it would change your stock and material reports.';
};

export const guardBankAccount = (id: string): Guard => async () => {
  const [out, inn] = await Promise.all([
    count('fund_transfers', 'from_account_id', id),
    count('fund_transfers', 'to_account_id', id),
  ]);
  const total = out + inn;
  if (total === 0) return null;
  return `This account is used by ${plural(total, 'transfer')}. ` +
    'Removing it would leave those transfers pointing nowhere.';
};

/** Called by the mutations, so a blocked delete can never slip through. */
export async function assertDeletable(guard: Guard): Promise<void> {
  const reason = await guard();
  if (reason) throw new Error(reason);
}
